import {
  filter,
  findIndex,
  indexOf,
  isFunction,
  partial,
  reject,
  sortBy,
} from 'lodash';

import type { LocalStorage } from '@shared/app/scripts/services/local-storage';
import type { NavigateTo } from '@core/app/scripts/services/navigate-to';
import { Broadcaster } from '@core/app/scripts/services/broadcaster';
import { CashierManager } from '@pos/app/scripts/services/cashier-manager';
import { CurrentSession } from '@shared/app/scripts/services/current-session';
import { DiscountRulesManager } from '@pos/app/scripts/services/discount-rules-manager';
import { DisplayMessage } from '@core/app/scripts/services/display-message';
import { EndOfShift } from '@pos/app/scripts/services/end-of-shift';
import { FocusStream } from '@pos/app/scripts/services/focus-streams';
import { GetDataModel } from '@pos/app/scripts/services/get-data-model';
import { HandleData } from '@core/app/scripts/services/handle-data';
import { LegacyCustomer } from '@pos/app/scripts/services/legacy-consumer';
import { LastTransaction } from '@pos/app/scripts/services/last-transaction';
import { LocationManager } from '@shared/app/scripts/services/location-manager';
import { Mask } from '@core/app/scripts/services/mask';
import { Persist } from '@core/app/scripts/services/persist';
import { PosState } from '@pos/app/scripts/services/pos-state';
import { SaleModel } from '@shared/app/scripts/classes/sale-model';
import { SalesManager } from '@shared/app/scripts/services/sales-manager';
import { Sentry } from '@shared/app/scripts/services/sentry';
import { SettingsManager } from '@shared/app/scripts/services/settings-manager';
import { SalesCalculations } from '@shared/app/scripts/services/sales-calculations';
import { Sku } from '@core/app/scripts/services/sku';
import { Utility } from '@core/app/scripts/services/utility';
import { WithdrawalSummary } from '@pos/app/scripts/services/withdrawal-summary';
import { WriteTransaction } from '@pos/app/scripts/services/write-transaction';
import {
  FashionCheque,
  FashionChequeResponse,
} from '@pos/app/scripts/services/fashion-cheque';

/**
 * @ngdoc service
 * @name posApp.pos
 * @description
 * Service hosting basic pos flow - management of the current transaction.
 */
angular
  .module('posApp')
  .factory(
    'pos',
    (
      $log: ng.ILogService,
      $q: ng.IQService,
      $timeout: ng.ITimeoutService,
      SaleModel,
      broadcaster: Broadcaster,
      cashierManager: CashierManager,
      cashierSession,
      consignment,
      currentSession: CurrentSession,
      dateFilter: ng.IFilterDate,
      discountRulesManager: DiscountRulesManager,
      displayMessage: DisplayMessage,
      endOfShift: EndOfShift,
      focusStreams: FocusStream,
      getDataModel: GetDataModel,
      handleData: HandleData,
      lastTransaction: LastTransaction,
      legacy,
      legacyCustomer: LegacyCustomer,
      localStorage: LocalStorage,
      locationManager: LocationManager,
      mask: Mask,
      navigateTo: NavigateTo,
      numberFilter: ng.IFilterNumber,
      persist: Persist,
      posInstance,
      posState: PosState,
      retailTransactions,
      sales,
      salesManager: SalesManager,
      secondScreen,
      sentry: Sentry,
      settingsManager: SettingsManager,
      sku: Sku,
      salesCalculations: SalesCalculations,
      transitOrderSummary,
      translate: ng.translate.ITranslateService,
      utility: Utility,
      withdrawalSummary: WithdrawalSummary,
      writeTransaction: WriteTransaction,
      fashionCheque: FashionCheque,
    ) => {
      const $translate = translate;

      const pos: any = {};

      /**
       * @ngdoc
       * @name posApp.pos#config
       * @propertyOf posApp.pos
       *
       * @description
       * Config/global object containing data related to transactions
       */
      pos.config = {
        // TODO replace handleData by more SID specific service
        token: handleData.getSID(), // session token
        selectedPayment: '', // Payment selected on the checkout form
        tenant_id: currentSession.tenantId,
        // TODO replace with cashierSession.cashier
        selectedCashier: null, // current cashier
        cashregisterNr: 1,
        transitDirection: '',
        datafeed: '', // tenant specific feedfolder
      };

      /**
       * @ngdoc
       * @name posApp.pos#db
       * @propertyOf posApp.pos
       * @description
       * Plain old global object
       */
      pos.db = {};

      // TODO to be wrapped as visual fields
      pos.selectedMenu = 1; // nav menu
      pos.selectedPayment = '';
      pos.returnArticleMode = false;
      pos.disabledByPayment = false;

      // TODO rename to resetCashier
      pos.onChangeCashier = () => {
        cashierSession.reset();
        pos.config.selectedCashier = null;
        // Deliberately keep the last cashier stamped on the in-progress
        // transaction: async payment callbacks (e.g. a coupled PIN terminal
        // completing late) may save it while the POS waits at the pin pad.
        // changeCashier() re-stamps it once the next cashier authenticates.
        navigateTo('pos.route#main');
      };

      // TODO rename to searchBarcode or search
      pos.onPosSearch = (_barcode: string) => {
        let barcode = _barcode;

        if (barcode.substring(0, 1) == '#') {
          barcode = barcode.substring(1);
        }

        if (barcode === '' || barcode === '+') {
          return $q.when();
        }

        //This is a transaction receipt number
        if (barcode.startsWith('-')) {
          return pos.loadTransaction(barcode.substring(1));
        }

        //This is a transaction receipt number
        if (barcode.startsWith('-')) {
          return pos.loadTransaction(barcode.substring(1));
        }

        //This is a transaction receipt of ECWID
        if (barcode.startsWith('ORDER')) {
          return pos.loadTransaction(barcode);
        }

        //To search a customer begin with a dot or fill in an emailaddress
        if (barcode.startsWith('.') || barcode.indexOf('@') != -1) {
          const args: any = {};

          if (barcode.startsWith('.')) {
            barcode = barcode.substring(1);
          }
          //Check if email is passed
          if (String(barcode).indexOf('@') != -1) {
            args.email = barcode;
          } else {
            args.loyaltyNo = barcode;
          }

          pos.addCustomerToTransaction(args);

          return $q.when();
        }

        const reqid = salesCalculations.addTemporaryReceiptLine(
          pos.transaction,
          {
            found: false,
            category: 'none',
            barcode,
            qty: 0,
            price: 0,
            nettPrice: 0,
            vat: 0,
          },
        );

        if (barcode.startsWith('+')) {
          return pos.getCoupon(barcode.substring(1), reqid);
        }

        if (barcode.length === 19) {
          if (fashionCheque.validateCode(barcode)) {
            displayMessage.showLoading();

            return fashionCheque
              .checkVoucher(barcode)
              .then((response: FashionChequeResponse) => {
                if (response.status !== 'ok') {
                  pos.removeItemFromReceipt(reqid);
                  return $q.reject(
                    response.data?.message || 'CheckFashionChequeError',
                  );
                }

                const amount = response.data.card.amount.value;

                if (amount <= 0) {
                  pos.removeItemFromReceipt(reqid);
                  return $q.reject($translate.instant('VoucherNoValue'));
                }

                const alreadyScanned = pos.transaction.receipt.some(
                  (receiptLine: any) => receiptLine.couponNr === barcode,
                );

                if (alreadyScanned) {
                  pos.removeItemFromReceipt(reqid);
                  return $q.reject('AlreadyScanned');
                }

                // successful fashion cheque scan
                const receipt = salesCalculations.addFashionChequeToRecepit(
                  response,
                  pos.transaction,
                  reqid,
                );

                if (receipt) {
                  pos.updateTotals();
                  broadcaster.send('forceScroll');
                  broadcaster.send('windowResized');
                }

                mask.hide();
              })
              .catch((error: FashionChequeResponse) => {
                const errorMessage =
                  typeof error === 'string'
                    ? error
                    : error.data?.message || 'CheckFashionChequeError';

                displayMessage.message($translate.instant(errorMessage));
                pos.removeItemFromReceipt(reqid);
                mask.hide();
                return $q.reject(error);
              });
          } else {
            displayMessage.message($translate.instant('VoucherNotRecognized'));
            mask.hide();
            return $q.reject('VoucherNotRecognized');
          }
        }

        return sku.get(barcode).then(
          (data) => {
            let message;

            //If data is not an object then do not process
            if (!angular.isObject(data)) {
              message = $translate.instant('BarcodeNotFound');
              displayMessage.message(message);

              pos.removeItemFromReceipt(reqid);

              return;
            }

            const qty = pos.returnArticleMode ? -1 : 1;
            pos.returnArticleMode = false;

            if (data.error == 'Barcode not found') {
              message = $translate.instant('BarcodeNotFound');
              displayMessage.message(message + data.barcode);

              pos.removeItemFromReceipt(reqid);

              return;
            }

            let itemIndex: any = findIndex(pos.transaction.receipt, {
              reqid,
            });

            if (itemIndex === -1) {
              $log.error('Barcode object could not be located in receipt!');
              return;
            }

            let item = pos.transaction.receipt[itemIndex];

            item.found = true;
            item.category = 'barcode';
            item.barcode = data.barcode ? data.barcode : barcode;
            item.articleCode = data.articleCode;
            item.articleDescription = data.articleDescription;
            item.sizeLabel = data.sizeLabel;
            item.brand = data.brand ? data.brand : null;
            item.collection = data.collection ? data.collection : null;
            item.group = data.group ? data.group : null;
            item.price = data.saleprice ? data.saleprice : data.price;
            item.nettPrice = data.price;
            item.color = data.color;

            item.vat = isNaN(data.vat)
              ? settingsManager.settings.vat.highvalue
              : data.vat;
            item.changeDisc = false;
            item.qty = qty;
            item.image = data.image;

            pos.transaction.totalAmount += utility.round2(
              qty * parseFloat(data.price),
            );
            pos.transaction.selectedLine = itemIndex;

            // process discount rules
            if (!pos.transaction.transit) {
              try {
                pos.procDiscountRules();
              } catch (e) {
                $log.error(
                  '[Automated Discounts] Error processing discount rules!',
                );
                $log.error(e);
              }
            }

            //Update all totals
            pos.updateTotals();

            //Scroll to the last item
            broadcaster.send('forceScroll');

            //Checkout Screen has to be resized
            broadcaster.send('windowResized');
          },
          (error) => {
            $log.error('Failed to Find Barcode ' + error);
            const message = $translate.instant('ErrorScanningBarcode', {
              barcode,
            });
            displayMessage.message(message);
            const tempArray = pos.transaction.receipt.slice(0);
            pos.transaction.receipt = reject(
              tempArray,
              (item) => item.reqid == reqid,
            );
          },
        );
      };

      const procCustomer = (customerdata) => {
        if (customerdata._id) {
          pos.customer = customerdata;

          //consignments open
          pos.checkForOpenConsignments(customerdata._id);

          // Check if customer has open coupons
          broadcaster.send('posGetCustomerOpenCoupons', customerdata._id);

          //Only when selecting a customer
          pos.setTransactionDetails(pos.transaction, null, null, customerdata);

          //Loyalty Points Balance
          pos.loadLoyaltyBalance(customerdata._id);

          //customer store credit
          pos.loadStoreCreditBalance(customerdata._id);

          // calculate customer points
          pos.updateTotals();
        }
      };

      /**
       * @ngdoc
       * @name posApp.pos#addCustomerToTransaction
       * @methodOf posApp.pos
       * @description
       * This method adds the requested customer to the sales transaction
       * @param {object} args
       * arguments packed into one object:
       * {
       *   email: {string} # the customer's email address
       *   loyaltyNo: {string} # the customer's loyalty program number
       *   id: # the customer's id
       * }
       */
      pos.addCustomerToTransaction = (args) => {
        if (!args && (!args.email || !args.id || !args.loyaltyNo)) {
          $log.error(
            '[addCustomerToTransaction] missing id, email, or loyaltyNo!',
          );
          return;
        }

        //Select a customer
        if (
          pos.transaction.customer &&
          pos.transaction.customer.loyaltynr &&
          pos.transaction.customer.loyaltynr == args.loyaltyNo
        ) {
          $log.log('[addCustomerToTransaction] Same customer loyaltynr!');
          return;
        }

        if (
          pos.transaction.customer &&
          pos.transaction.customer.id &&
          pos.transaction.customer.id == args.id
        ) {
          $log.log('[addCustomerToTransaction] Same customer id!');
          return;
        }

        if (
          pos.transaction.customer &&
          pos.transaction.customer.email &&
          pos.transaction.customer.email == args.email
        ) {
          $log.log('[addCustomerToTransaction] Same customer email address!');
          return;
        }

        if (pos.transaction.customer) {
          const callback = (result) => {
            if (result == 'yes') {
              //Drop the entire transaction.
              pos.resetTransaction('changeClient');
              navigateTo('pos.route#main');
            }
          };
          const message = $translate.instant('CustomerSelectedDropTransaction');
          displayMessage.message({
            message: message,
            type: 'prompt',
            callback: callback,
          });
          return;
        }

        legacyCustomer
          .get(args.id, args.email, args.loyaltyNo)
          .then(procCustomer);
      };

      /*
       * Process found coupons and add them to the receipt array
       * @reqid Random request ID
       * @data The response returned from the API
       */
      pos.procCoupon = (reqid, response) => {
        mask.hide();

        let item: any = filter(pos.transaction.receipt, {
            reqid: reqid,
          }),
          tempArray;

        if (item.length === 0) {
          $log.error('Coupon object could not be located in receipt!');
          return;
        }
        item = item[0];

        const couponType = response.coupontype;
        // CouponvValuePercentage is only used for percentage coupons.
        const couponValuePercentage = response.value;
        let couponValue = response.value / 100;
        const couponValueType = response.type;
        const customerId = response.customerUuid;
        const couponNr = response.coupon;

        if (response.error == 'Invalid coupon') {
          displayMessage.message($translate.instant('WrongCode'));
          item.price = 0;
          item.nettPrice = 0;
          item.found = false;
          item.category = 'none';
          tempArray = pos.transaction.receipt.slice(0);

          pos.transaction.receipt = reject(
            tempArray,
            (item) => item.reqid == reqid,
          );

          return;
        }

        // couponType = 'U', 'cadeaubonnen', gift card - even if it's associated
        // with an account, it's usually bought for other people - the customer
        // shouldn't be set automatically
        if (customerId !== '' && couponType != 'U') {
          if (
            pos.transaction.customer?.id &&
            pos.transaction.customer.id !== customerId
          ) {
            displayMessage.message(
              $translate.instant('ScannedCouponOfOtherCustomer'),
            );

            pos.transaction.receipt = reject(
              pos.transaction.receipt,
              (item) => item.reqid == reqid,
            );

            return;
          } else {
            legacyCustomer.get(customerId).then(procCustomer);
          }
        }

        item.found = true;
        item.category = 'coupon';
        item.reqid = reqid;
        item.couponNr = couponNr;

        const capToReceiptTotal =
          settingsManager.settings.pos?.capVoucherRedemptionToReceiptTotal ===
          true;
        const outstandingAmount = Math.max(
          0,
          utility.round2(salesCalculations.total(pos.transaction) || 0),
        );
        const shouldCapToReceiptTotal =
          capToReceiptTotal && outstandingAmount > 0;
        const effectiveCouponValue = shouldCapToReceiptTotal
          ? Math.min(couponValue, outstandingAmount)
          : couponValue;

        item.price = effectiveCouponValue;
        item.qty = 1;
        item.nettPrice = effectiveCouponValue;
        item.type = couponType;

        //Check for duplicates
        const dups = filter(pos.transaction.receipt, {
          couponNr: item.couponNr,
        });

        if (dups.length > 1) {
          displayMessage.message($translate.instant('AlreadyScanned'));
          item.price = 0;
          item.nettPrice = 0;
          item.found = false;
          item.category = 'none';
          tempArray = pos.transaction.receipt.slice(0);
          pos.transaction.receipt = reject(
            tempArray,
            (item) => item.reqid == reqid,
          );
          return;
        }

        //From here, coupon is deemed correct
        if (couponType == ' ' || couponType == 'C') {
          // This coupon is kept in receipt array, but it's values are zeroed out. Instead, values are added to
          // couponDiscount and totalDiscount.
          item.price = 0;
          item.nettPrice = 0;
          if (couponValueType == 'percentage') {
            const totalWithDiscount =
              pos.transaction.totalAmount - pos.transaction.totalDiscount;
            // Make sure the discount is not higher than totalWithDiscount.
            item.value = Math.min(
              Math.round(totalWithDiscount * couponValuePercentage) / 100,
              Math.round(totalWithDiscount * 100) / 100,
            );
            item.articleDescription =
              $translate.instant('Value ') +
              couponValuePercentage +
              '% ' +
              $translate.instant('Of') +
              ' ' +
              totalWithDiscount +
              ' = ' +
              numberFilter(item.value, 2);
          } else {
            item.value = effectiveCouponValue;
            item.articleDescription =
              $translate.instant('Value') +
              ': ' +
              numberFilter(effectiveCouponValue, 2);
          }

          if (pos.transaction.couponDiscount) {
            pos.transaction.couponDiscount += item.value;
          } else {
            pos.transaction.couponDiscount = item.value;
          }
          pos.transaction.totalDiscount += item.value;
        }

        //Update all totals
        pos.updateTotals();

        //Scroll to the last item
        broadcaster.send('forceScroll');
      };

      /*
       * Attempt to retry failed coupons once. If cannot find, remove line and
       * display error.
       * @reqid Random ID of the request
       * @coupon Coupon barcode
       * @count The number of tries
       */
      const procCouponError = (reqid, coupon, count, error) => {
        //Retry once
        if (count == 1) {
          const t: any = $timeout(
            () => {
              $log.log('Retrying failed coupon');
              legacy
                .getCoupon(coupon)
                .then(
                  partial(pos.procCoupon, reqid),
                  partial(procCouponError, reqid, coupon, 2),
                );

              clearTimeout(t);
            },
            2000,
            true,
          );
          return;
        }

        $log.log('Failed to Find Coupon ' + coupon + ' After Retry: ');
        mask.hide();

        const message = $translate.instant('ErrorScanningCoupon', { coupon });
        displayMessage.message(message);
        const tempArray = pos.transaction.receipt.slice(0);

        pos.transaction.receipt = reject(
          tempArray,
          (item) => item.reqid == reqid,
        );
      };

      /*
       * Method to retrieve coupons from the API
       */
      // TODO move to a private method
      pos.getCoupon = (coupon, reqid) => {
        if (!coupon) {
          return;
        }

        if (
          coupon != 'kadobon' &&
          coupon != 'vvvbon' &&
          coupon != 'fashioncheque' &&
          coupon != 'kortingbon' &&
          coupon != 'tegoedbon'
        ) {
          //Coupons should be uppercase
          coupon = coupon.toUpperCase();
        }

        displayMessage.showLoading();

        return legacy
          .getCoupon(coupon)
          .then(
            partial(pos.procCoupon, reqid),
            partial(procCouponError, reqid, coupon, 1),
          );
      };

      /*
       * Function for buffering transactions
       * @param {Object} transaction
       */
      function bufferTransaction(transaction) {
        writeTransaction.buffer(transaction, (buffer_id) => {
          transaction.buffer_id = buffer_id;
        });
      }

      /**
       * @ngdoc
       * @name posApp.pos#startNewTransaction
       * @methodOf posApp.pos
       * @param callback
       * Success callback
       * @description
       * Method that creates a new transaction model
       */
      pos.startNewTransaction = () => {
        pos.transaction = new SaleModel(getDataModel.getTransaction());

        pos.transaction = pos.setWarehouse(pos.transaction);

        posState.returnTransaction = undefined;

        // TODO SWUI-1646 move this field to hardcoded model
        pos.transaction.overallReceiptDiscount = 0;
      };

      /**
       * @ngdoc
       * @name posApp.pos#setTransactionDetails
       * @methodOf posApp.pos
       * @param {object} transaction the sales transaction
       * @param {object} cashier the cashier object - deprecated, use pos.setCashier directly
       * @param {object} warehouse the warehouse/shop object - deprecated, use pos.setWarehouse directly
       * @param {object} customer the optional customer object
       * @param {number} loyaltyPoints the optional loyalty points value
       * @returns {object} modified transaction
       * @description
       * Method write common properties to a transaction
       */
      // TODO cover with unit tests
      pos.setTransactionDetails = (
        transaction,
        cashier,
        dummyWarehouse,
        customer,
        loyaltyPoints,
      ) => {
        if (!transaction) {
          $log.error('[refreshTransactionDetails] Missing transaction!');
          return;
        }

        transaction = pos.setWarehouse(transaction);

        //Set Cashier properties
        // TODO deprecate the cashier parameter
        if (cashier) {
          transaction = pos.setCashier(transaction, cashier);
        }

        //Set Customer properties including email from the contacts
        if (customer) {
          if (!transaction.customer) {
            transaction.customer = {};
          }

          transaction.customer.title = customer.title;
          transaction.customer.firstname = customer.first_name;
          transaction.customer.middlename = customer.middle_name;
          transaction.customer.lastname = customer.last_name;
          //New Id
          transaction.customer.id = customer._id;
          //Legacy Id
          // TODO POS-1420 phase out custnum from transaction
          transaction.customer.custnum = customer.cust_id;
          //Loyalty card number
          transaction.customer.loyaltynr = customer.loyalty_no;

          for (const c in customer.contacts) {
            const custContact = customer.contacts[c];
            //Use only the primary email if it exists
            if (custContact.primary) {
              transaction.customer.email = custContact.email;
              custContact.mobile
                ? (transaction.customer.telephone = custContact.mobile)
                : (transaction.customer.telephone = custContact.phone);
            }
          }

          $log.log(
            '[refreshTransactionDetails] Transaction Customer Updated:',
            customer.last_name,
          );
        }

        if (loyaltyPoints) {
          if (!transaction.customer) {
            transaction.customer = {};
          }
          transaction.customer.points = loyaltyPoints;
          $log.log(
            '[refreshTransactionDetails] Transaction Loyalty Points Update:',
            loyaltyPoints,
          );
        }

        return transaction;
      };

      /**
       * @ngdoc
       * @name posApp.pos#setWarehouse
       * @methodOf posApp.pos
       * @param {object} transaction the sales transaction
       * @returns {object} modified transaction
       * @description
       * Method write common properties to a transaction - warehouse
       */
      // TODO to be covered by tests
      pos.setWarehouse = (transaction) => {
        if (!transaction?.shop) {
          return transaction;
        }

        transaction.shop.id = locationManager.current.wh;
        transaction.shop.name = locationManager.current.name;
        transaction.shop.street = locationManager.current.street;
        transaction.shop.houseno = locationManager.current.houseno;
        transaction.shop.houseadd = locationManager.current.houseadd;
        transaction.shop.city = locationManager.current.city;
        transaction.shop.zipcode = locationManager.current.zipcode;
        transaction.shop.phone = locationManager.current.phone;
        transaction.shop.email = locationManager.current.email;
        transaction.shop.website = locationManager.current.website;
        transaction.shop.bankAccountNumber =
          locationManager.current.bankAccountNumber;

        $log.log(
          '[refreshTransactionDetails] Transaction Shop Updated:',
          locationManager.current.name,
        );

        return transaction;
      };

      /**
       * @ngdoc
       * @name posApp.pos#setCashier
       * @methodOf posApp.pos
       * @param {object} transaction the sales transaction
       * @param {object} cashier the cashier object
       * @returns {object} modified transaction
       * @description
       * Method write common properties to a transaction - cashier
       */
      // TODO move to SalesCalculations
      pos.setCashier = (transaction, cashier) => {
        if (!cashier.name) {
          sentry.captureMessage(
            'Cashier Error (POS-1030)! cashier: ' + JSON.stringify(cashier),
          );
        }

        if (!transaction.cashier) {
          transaction.cashier = {};
        }

        transaction.cashier.id = cashier._id;
        transaction.cashier.fullname = cashier.fullname;
        transaction.cashier.name = cashier.name;
        $log.log(
          '[refreshTransactionDetails] Transaction Cashier Updated:',
          cashier.fullname,
        );

        return transaction;
      };

      /**
       * @ngdoc
       * @name posApp.pos#loadTransaction
       * @methodOf posApp.pos
       * @param {string} nr
       * transaction id
       * @description
       * Method to retrieve receipts by receipt number. This is used when scanning
       * a barcode on the receipt printout.
       */
      pos.loadTransaction = (nr: number) => {
        displayMessage.showLoading();
        // get barcode first from mongo

        const noSuccessFindTransaction = () => {
          const message = $translate.instant('CannotLocateTransaction');
          displayMessage.message(message);
        };

        return retailTransactions
          .filter({ nr, type: [2, 9] })
          .then((response) => {
            mask.hide();
            let transaction: any;

            if (response && response.length > 0) {
              transaction = response[0];

              //Check if this is a closed transaction (used in consignment)
              if (transaction.status === 'closed') {
                displayMessage.message(
                  $translate.instant('TransactionAlreadyClosed'),
                );
                return;
              }

              posState.returnTransaction = angular.copy(transaction);

              // Clear the buffer_id
              delete transaction.buffer_id;

              delete transaction.fiscal_receipt_nr;
              delete transaction.fiscal_shift_nr;
              delete transaction.fiscal_date;
              delete transaction.fiscal_printer_id;
              delete transaction.fiscalQrUrl;
              delete transaction.fiskaly_invoice_id;
              delete transaction.fiskaly_api_response;

              transaction.return_original_nr = posState.returnTransaction.nr;

              //create linkage between old receipt and new receipt
              if (transaction.type == 2) {
                //If type 2 (sale) then this should be treated as a return
                transaction.link = utility.link(
                  'transactions',
                  transaction._id,
                  'return',
                  transaction.nr,
                );

                //redistribute discounts POS-248
                salesCalculations.preCalc(transaction);
                salesCalculations.spreadTotalDiscount(transaction);

                // reset discount that was applied on previous transaction
                transaction.overallReceiptDiscount = 0;

                for (const l in transaction.receipt) {
                  if (
                    transaction.receipt[l] &&
                    transaction.receipt[l].category == 'barcode'
                  ) {
                    //if type 2 (sale) then quantities should be 0
                    transaction.receipt[l].qty = 0;
                  }
                }
              } else if (transaction.type == 9) {
                //if type 9 (consignment) then it should be treated as a new sale
                transaction.link = utility.link(
                  'transactions',
                  transaction._id,
                  'consignment',
                  transaction.nr,
                );
              } else {
                displayMessage.message(
                  $translate.instant('TransactionIsNotSalesNorConsignment'),
                );
                return;
              }

              pos.reloadTransaction(transaction);
            } else {
              noSuccessFindTransaction();
            }
          }, noSuccessFindTransaction);
      };

      // process discount rules
      pos.procDiscountRules = () => {
        let a;

        function resetActionDiscount(rownum) {
          const totalDiscount =
            (pos.transaction.receipt[rownum].price -
              pos.transaction.receipt[rownum].nettPrice) *
            pos.transaction.receipt[rownum].qty;
          pos.transaction.receipt[rownum].price =
            pos.transaction.receipt[rownum].nettPrice;
        }

        // check for discount rules
        if (
          !discountRulesManager.data ||
          discountRulesManager.data.length === 0
        ) {
          return;
        }

        const nrRules = discountRulesManager.data.length;

        // copy the array pos receipt
        const copyPosReceipt = angular.copy(pos.transaction.receipt);
        let nrReceiptLines = copyPosReceipt.length;
        if (nrReceiptLines === 0) {
          return;
        }

        // get priorities
        let line;
        let rule;

        for (let j = 0; j < nrReceiptLines; j++) {
          line = copyPosReceipt[j];
          line.rownum = j;
          line.rules = [];
          for (let i = 0; i < nrRules; i++) {
            rule = discountRulesManager.data[i];

            if (
              line.changeDisc ||
              line.category !== 'barcode' ||
              // ignore return or empty lines
              line.qty <= 0
            ) {
              line.rules[i] = Number.MAX_VALUE;
            } else if (
              (!rule.articles ||
                !rule.articles.length ||
                indexOf(rule.articles, line.articleCode) != -1) &&
              (!rule.groups ||
                !rule.groups.length ||
                indexOf(rule.groups, line.group) != -1) &&
              (!rule.collections ||
                !rule.collections.length ||
                indexOf(rule.collections, line.collection) != -1) &&
              (!rule.brands ||
                !rule.brands.length ||
                indexOf(rule.brands, line.brand) != -1) &&
              (!rule.shops ||
                !rule.shops.length ||
                indexOf(rule.shops, locationManager.current.name) != -1)
            ) {
              line.rules[i] = rule.priority;
            } else {
              line.rules[i] = Number.MAX_VALUE;
            }
          }
        }

        // TODO this function works by having `a` as leaked in value
        const prioritySorting = (x, y) => {
          if (x.rules[a] == y.rules[a]) {
            if (x.nettPrice == y.nettPrice) {
              return 0;
            }
            if (x.nettPrice < y.nettPrice) {
              return -1;
            }
            if (x.nettPrice > y.nettPrice) {
              return 1;
            }
          }

          if (x.rules[a] < y.rules[a]) {
            return -1;
          }
          if (x.rules[a] > y.rules[a]) {
            return 1;
          }

          return 0;
        };

        // group articles by priority
        for (a = 0; a < nrRules; a++) {
          // sort articles by priority and sell price
          copyPosReceipt.sort(prioritySorting); // sorted article list

          // group by priority
          const priority = discountRulesManager.data[a].priority;
          const type = discountRulesManager.data[a].type;
          const x = parseInt(discountRulesManager.data[a].x);
          const y = parseFloat(discountRulesManager.data[a].y);

          switch (type) {
            case 'x4y':
            case 'x4ye':
              for (
                let p = 0, cnt = 0, tQty = 0, tAmount = 0;
                p < nrReceiptLines;
                p++
              ) {
                if (copyPosReceipt[p].rules[a] == priority) {
                  copyPosReceipt[p].price = copyPosReceipt[p].nettPrice;
                  resetActionDiscount(copyPosReceipt[p].rownum);
                  copyPosReceipt[p].discountreason =
                    'Kortingregel: ' + discountRulesManager.data[a].name;
                  cnt++;
                  tQty += copyPosReceipt[p].qty;
                  tAmount +=
                    copyPosReceipt[p].nettPrice * copyPosReceipt[p].qty;

                  if (tQty > 0 && x !== 0 && tQty % x === 0) {
                    // calculate discount_actionprices
                    procActionDiscounts(
                      copyPosReceipt.splice(0, cnt),
                      type,
                      x,
                      y,
                      tAmount,
                    );

                    nrReceiptLines = copyPosReceipt.length;

                    cnt = 0;
                    p = -1;
                    tQty = 0;
                    tAmount = 0;
                  }
                } else {
                  break;
                }
              }

              break;
            case 'xyp':
            case 'gtxyp':
              let q = 0,
                last = 0,
                total = 0;
              if (nrReceiptLines > 0) {
                do {
                  copyPosReceipt[q].price = copyPosReceipt[q].nettPrice;
                  if (
                    copyPosReceipt[q].rules[a] == priority &&
                    !copyPosReceipt[q].changeDisc
                  ) {
                    resetActionDiscount(copyPosReceipt[q].rownum);
                    copyPosReceipt[q].discountreason =
                      'Kortingregel: ' + discountRulesManager.data[a].name;

                    total += copyPosReceipt[q].qty;
                    last = q;
                  } else {
                    break;
                  }
                  q++;
                } while (q < nrReceiptLines);
              }

              if (total >= x) {
                // calculate discount_actionprices
                procActionDiscounts(
                  copyPosReceipt.splice(0, last + 1),
                  type,
                  x,
                  y,
                  0,
                  // will be ignored by gtxyp
                  Math.floor(total / x) * x,
                );
                nrReceiptLines = copyPosReceipt.length;
              }
          }
        }
      };

      /**
       * procces action discount. Requires the items used to have a rownum field.
       * (ie: you can't just push the default receipt list in)
       * @param {array} prioList list with items to discount on.
       * @param {String} type x4y, x4ye, xyp, gtxyp.
       * x4ye: listed pieces for y euro. Uses tAmount to calculate discount per item.
       * xyp: listed pieces each get y% off of their price.
       * gtxyp: does exactly the same as xyp
       * @param {Number} x only used in x4y.
       * @param {Number} y value, meaning depends on type.
       * x4ye: amount of money
       * @param {Number} tAmount total price, ignored in xyp and gtxyp. Can in theory also be calculated within this function.
       * @param {Number} discountQ number of pieces that are supposed to be discounted. Used only in xyp
       */
      // TODO refactor the method out
      function procActionDiscounts(prioList, type, x, y, tAmount, discountQ?) {
        let totalDiscount = 0,
          totalDiscount2 = 0,
          calc,
          maxList,
          q,
          discount,
          discountRounded;

        /*
         * give action discount
         */
        function giveActionDiscount(rownum, discount, discountRounded) {
          totalDiscount +=
            pos.transaction.receipt[rownum].price -
            pos.transaction.receipt[rownum].nettPrice +
            discount * pos.transaction.receipt[rownum].qty;
          totalDiscount2 +=
            pos.transaction.receipt[rownum].price -
            pos.transaction.receipt[rownum].nettPrice +
            discountRounded * pos.transaction.receipt[rownum].qty;

          pos.transaction.receipt[rownum].price = utility.round2(
            pos.transaction.receipt[rownum].nettPrice - discountRounded,
          );
          pos.transaction.receipt[rownum].changeDisc = false;
        }
        if (y > 0) {
          switch (type) {
            case 'x4y':
              const fnSortPrice = (m, n) => {
                if (m.nettPrice < n.nettPrice) {
                  return -1;
                }
                if (m.nettPrice > n.nettPrice) {
                  return 1;
                }
                return 0;
              };
              prioList.sort(fnSortPrice); // sort by sell price
              //N: ... 2 sorts? On the same attribute? ?_?
              prioList = sortBy(prioList, (article) => article.nettPrice);

              const t = x - y;
              calc = 0;
              for (let cnt = 0, c = 0; c < t; cnt++) {
                // get calculator
                if (t == 1) {
                  calc += prioList[0].nettPrice;
                  c++;
                } else {
                  if (prioList[cnt].qty > t - c) {
                    calc += (t - c) * prioList[cnt].nettPrice;
                    c += t - c;
                  } else {
                    calc += prioList[cnt].qty * prioList[cnt].nettPrice;
                    c += prioList[cnt].qty;
                  }
                }
              }
              maxList = prioList.length; // give discount

              for (q = 0, discount; q < maxList; q++) {
                discount = (calc / tAmount) * prioList[q].nettPrice;
                discountRounded = utility.round2(discount);
                prioList[q].price = utility.round2(
                  prioList[q].nettPrice - discountRounded,
                );
                pos.transaction.receipt[prioList[q].rownum].discountreason =
                  prioList[q].discountreason;
                giveActionDiscount(
                  prioList[q].rownum,
                  discount,
                  discountRounded,
                );
              }
              break;
            case 'x4ye':
              //calc: percentage price reduction
              calc = 1 - y / tAmount;
              maxList = prioList.length;
              for (q = 0, discount; q < maxList; q++) {
                //discount: flat price reduction on each item
                discount = calc * prioList[q].nettPrice;
                discountRounded = utility.round2(discount);
                prioList[q].price = utility.round2(
                  prioList[q].nettPrice - discountRounded,
                );
                giveActionDiscount(
                  prioList[q].rownum,
                  discount,
                  discountRounded,
                );
              }
              break;
            case 'xyp':
              maxList = prioList.length; // give discount

              for (q = 0; q < maxList; q++) {
                if (discountQ === 0) {
                  break;
                } else if (discountQ >= prioList[q].qty) {
                  discountQ -= prioList[q].qty;
                  calc = y / 100; // full discount
                } else {
                  // partial discount
                  calc = (y * discountQ) / prioList[q].qty / 100;

                  discountQ = 0;
                }

                discount = calc * prioList[q].nettPrice;
                discountRounded = utility.round2(discount);
                prioList[q].price = utility.round2(
                  prioList[q].nettPrice - discountRounded,
                );
                giveActionDiscount(
                  prioList[q].rownum,
                  discount,
                  discountRounded,
                );
              }
              break;
            case 'gtxyp':
              calc = y / 100; // get calculator
              maxList = prioList.length; // give discount
              for (q = 0, discount; q < maxList; q++) {
                discount = calc * prioList[q].nettPrice;
                discountRounded = utility.round2(discount);
                prioList[q].price = utility.round2(
                  prioList[q].nettPrice - discountRounded,
                );
                giveActionDiscount(
                  prioList[q].rownum,
                  discount,
                  discountRounded,
                );
              }
          }
        }

        // apply the ONE CENT rule
        const oneCent = utility.round2(totalDiscount - totalDiscount2);
        if (oneCent) {
          discount = prioList[0].nettPrice - prioList[0].price + oneCent;
          giveActionDiscount(
            prioList[0].rownum,
            discount,
            utility.round2(discount),
          );
        }
      }

      pos.removeItemFromReceipt = (reqid) => {
        let item: any = filter(pos.transaction.receipt, {
          reqid: reqid,
        });

        if (item.length === 0) {
          $log.error('Barcode object could not be located in receipt!');
          return;
        }

        item = item[0];
        item.price = 0;
        item.nettPrice = 0;
        item.found = false;
        item.category = 'none';
        const tempArray = pos.transaction.receipt.slice(0);
        pos.transaction.receipt = reject(
          tempArray,
          (item) => item.reqid == reqid,
        );
      };

      /**
       * @ngdoc
       * @name posApp.pos#updateTotals
       * @methodOf posApp.pos
       * @description
       * This method calculates all totals on provided transaction.
       * @param {object} transaction
       * Transaction to be edited. If not provided, defaults to the current pos.transacion
       */
      pos.updateTotals = (transaction) => {
        if (!transaction) {
          transaction = pos.transaction;
        }

        salesCalculations.updateTotals(transaction);
        salesCalculations.updateTotalPaid(transaction);
        salesCalculations.updateGrandTotal(
          transaction,
          settingsManager.settings.roundTotalAmount5cent,
        );

        //Validate the receipt totals
        pos.validateTransaction(transaction);

        // update vat
        salesCalculations.calculateVAT(
          transaction,
          settingsManager.settings.vat,
        );

        // calculate customer points
        salesCalculations.calculateLoyaltyPoints(
          transaction,
          settingsManager.settings.loyalty,
        );

        //persist transaction
        pos.persist('TRANS', pos.transaction);
        // TODO cover by test
        secondScreen.showTransaction(pos.transaction);
      };

      pos.updateTransaction = function () {
        // process discount rules
        try {
          pos.procDiscountRules();
        } catch (e) {
          $log.error('[Automated Discounts] Error processing discount rules!');
          $log.error(e);
        }

        //Broadcast change to update all totals
        pos.updateTotals();

        //Reset focus to scan
        focusStreams.searchSubject.next(undefined);
      };

      /*
       * This function validates a transaction to ensure all values are correct
       * If there is a problem an error will be displayed to the client and an
       * error report will be sent to Sentry
       * @param {Object} transaction - the transaction object
       */
      pos.validateTransaction = (transaction) => {
        function checkLines() {
          let pass = true;
          for (const i in transaction.receipt) {
            if (
              isNaN(transaction.receipt[i].qty) ||
              isNaN(transaction.receipt[i].nettPrice) ||
              isNaN(transaction.receipt[i].price) ||
              isNaN(transaction.receipt[i].vat)
            ) {
              pass = false;
              break;
            }
          }
          return pass;
        }

        function checkPayments() {
          let pass = true;
          for (const i in transaction.payments) {
            if (isNaN(transaction.payments[i])) {
              pass = false;
              break;
            }
          }
          return pass;
        }

        function checkVAT() {
          let pass = true;
          for (const i in transaction.vat) {
            if (isNaN(transaction.vat[i])) {
              pass = false;
              break;
            }
          }
          return pass;
        }

        $log.log('[Validate Transaction] Checking transaction amounts...');
        try {
          if (
            isNaN(transaction.totalAmount) ||
            isNaN(transaction.totalPaid) ||
            isNaN(transaction.totalCoupon) ||
            isNaN(transaction.totalDiscount)
          ) {
            const lines = checkLines();
            const pay = checkPayments();
            const vat = checkVAT();

            let error = 'Transaction Error! ';
            error += !lines
              ? 'Lines: ' + JSON.stringify(transaction.receipt)
              : '';
            error += !pay
              ? 'Payments :' + JSON.stringify(transaction.payments)
              : '';
            error += !vat ? 'VAT: ' + JSON.stringify(transaction.vat) : '';
            displayMessage.message($translate.instant('ErrorInReceipt'));

            sentry.captureMessage('Transaction Payment Error: Transaction', {
              extra: { transaction },
            });
          }
        } catch (err) {
          displayMessage.message($translate.instant('ErrorInReceipt'));
          sentry.captureMessage('Transaction Error.', {
            extra: { transaction },
          });
        }
      };

      // Return string that concatenate  all payment types on the transaction
      // TODO cover with tests & move away from pos service
      pos.paymentTypeString = () => {
        const payments = pos.transaction.payments;
        let transType = '';
        let p;
        for (p in payments) {
          if (payments[p] > 0) {
            transType += p;
          }
        }
        return transType;
      };

      // TODO rename to saveTransaction or saveSale+saveConsignment
      /**
       * @ngdoc
       * @name posApp.pos#sendReceipt
       * @methodOf posApp.pos
       * @description
       * This method saveTransaction on the spynl side, and update the consignment if
       * necessary
       */
      pos.sendReceipt = (skipSideEffects = false) => {
        let line;
        const deferred = $q.defer();

        displayMessage.showLoading();

        pos.transaction.device_id = pos.selectedDevice.device_id;

        // Calculate discount coupons
        let discount = 0;
        for (line in pos.transaction.receipt) {
          if (
            pos.transaction.receipt[line] &&
            pos.transaction.receipt[line].couponNr &&
            (pos.transaction.receipt[line].type == ' ' ||
              pos.transaction.receipt[line].type == 'C')
          ) {
            discount = discount + pos.transaction.receipt[line].price;
          }
        }

        //A sale receipt is any transaction where consignment is not used as a payment option
        const transactionType =
          pos.transaction.payments.consignment === 0 ? 'sale' : 'consignment';

        function transactionSaveCallback(transaction, transactionId) {
          if (
            isFunction(transaction.hasValidTotals) &&
            !transaction.hasValidTotals()
          ) {
            sentry.captureMessage('Saved invalid transaction', {
              extra: { transaction },
            });
          }

          // TODO add unit tests for resolved value
          deferred.resolve(transactionId);

          // Skip side effects for intermediate saves (save-twice pattern)
          if (!skipSideEffects) {
            //Reset transaction
            pos.resetTransaction(transactionType, transactionId);

            mask.hide();

            // TODO move the navigateTo to the controller
            //Finally Navigate to Pin Pad
            navigateTo('pos.route#main');

            //Resets any buttons that have been disabled
            broadcaster.send('posFinishedTransaction');

            pos.selectedMenu = 1;
          }
        }

        function transactionSaveFailed() {
          const message = $translate.instant('TransactionNotSuccessful');
          displayMessage.message(message);

          sentry.captureMessage(
            'Failed to save transaction, all data collected about it is going to be lost.',
            { extra: { transaction: pos.transaction } },
          );

          deferred.reject();
        }

        // link the shift if EOS is used, store receipt in the spynl api
        endOfShift.setShiftCycleId(pos.transaction);

        let transactionCopy: SaleModel = angular.copy(pos.transaction);

        transactionCopy = salesCalculations.setType(
          transactionCopy,
          transactionType,
        );

        // Override to pending for save-twice pattern (except consignments)
        // - First save: skipSideEffects is true
        // - Second save: transaction has _id (was saved before)
        // - Consignments: Always type 9 (cannot be partial, no pending state)
        // This lets the backend handle auto-promotion logic for sales
        if (
          (skipSideEffects || transactionCopy._id) &&
          transactionType !== 'consignment'
        ) {
          transactionCopy.type = 'pending';
        }

        // A transaction can lose its cashier when a save fires while the POS
        // waits at the pin pad (e.g. an async PIN payment completing after
        // "change cashier" was pressed). Re-stamp it if the cashier is still
        // known; otherwise abort the save with a clear message instead of
        // throwing an unhandled error that leaves the loading mask up.
        if (!transactionCopy.cashier?.id && pos.config.selectedCashier) {
          transactionCopy = pos.setCashier(
            transactionCopy,
            pos.config.selectedCashier,
          );
        }

        if (!transactionCopy.isValid()) {
          sentry.captureMessage(
            `[sendReceipt]: Invalid transaction for _id: ${transactionCopy._id}, save aborted`,
            { extra: { transaction: transactionCopy } },
          );

          displayMessage.message(
            $translate.instant('TransactionNotSavedNoCashier'),
          );

          deferred.reject();
          return deferred.promise;
        }

        // Remove backend-managed fields
        // Always remove: modified, created (backend timestamps these)
        // Keep: _id (needed for re-entrant updates in save-twice pattern)
        if (transactionCopy.modified) {
          delete transactionCopy.modified;
        }
        if (transactionCopy.created) {
          delete transactionCopy.created;
        }

        if (
          transactionCopy.type === 2 ||
          (transactionCopy.type === 'pending' && transactionType === 'sale')
        ) {
          console.log(
            '[sendReceipt] About to save transaction, type:',
            transactionCopy.type,
            '_id:',
            transactionCopy._id,
          );
          bufferTransaction(transactionCopy);

          // Check if sales.add is a function
          if (typeof sales.add !== 'function') {
            console.error('[sendReceipt] sales.add is not a function!');
            transactionSaveFailed();
            return deferred.promise;
          }

          console.log('[sendReceipt] Calling sales.add()');
          const salesAddPromise = sales.add(transactionCopy);

          salesAddPromise.then(
            (saved) => {
              const transactionId = Array.isArray(saved)
                ? saved[0]?._id || saved[0]
                : saved?._id || saved;
              transactionSaveCallback(transactionCopy, transactionId);
            },
            (error) => {
              transactionSaveFailed();
            },
          );
        } else if (
          transactionCopy.type === 9 ||
          (transactionCopy.type === 'pending' &&
            transactionType === 'consignment')
        ) {
          bufferTransaction(transactionCopy);
          consignment.add(transactionCopy).then((saved) => {
            transactionSaveCallback(transactionCopy, saved[0]);
          }, transactionSaveFailed);
        } else {
          // Transactions using this method should be either type 2, 9, or pending
          transactionSaveFailed();
        }

        return deferred.promise;
      };

      /**
       * Save transaction as pending (first save in save-twice pattern).
       * Sets pos.transaction._id for subsequent update.
       * Skips side effects (no reset/navigation).
       */
      pos.saveAsPending = () => {
        return pos.sendReceipt(true).then((transactionId) => {
          // CRITICAL: Set _id on pos.transaction for second save
          pos.transaction._id = transactionId;

          // CRITICAL: Verify transaction was saved and get server state
          // This ensures we have the correct _id and can detect auto-promotion
          // Note: Backend may not return type=='pending' transactions, so empty response
          // should be treated as "still pending" and we continue the flow
          return sales
            .get(transactionId)
            .then((savedTransaction) => {
              // Handle case where sales.get returns empty array (backend filters pending)
              // Check if savedTransaction is an array or if it's null/undefined
              if (
                Array.isArray(savedTransaction) &&
                savedTransaction.length === 0
              ) {
                // Empty array response - transaction is still pending
                // Continue with the flow using the transactionId we got from the save
                return transactionId;
              }
              // Verify the transaction exists and update _id if needed
              if (savedTransaction && savedTransaction._id) {
                pos.transaction._id = savedTransaction._id;
              }
              return transactionId;
            })
            .catch((error) => {
              // If sales.get rejects (e.g., 'Not found'), treat it as "still pending"
              if (
                error === 'Not found' ||
                (typeof error === 'string' && error.includes('Not found'))
              ) {
                // Transaction is still pending (backend doesn't return pending transactions)
                // Continue with the flow using the transactionId we got from the save
                return transactionId;
              }
              // For other errors, re-throw to let error handling continue
              throw error;
            });
        });
      };

      /**
       * Update transaction with final data and fetch saved version.
       * Triggers side effects (reset/navigation).
       * Used as second save in save-twice pattern.
       * Smart: Skips second save if transaction already auto-promoted to type 2.
       */
      pos.updateAndFinalize = () => {
        console.log(
          '[updateAndFinalize] Starting, transaction._id:',
          pos.transaction._id,
        );
        // First, check if transaction was already auto-promoted
        return sales
          .get(pos.transaction._id)
          .then((currentTransaction) => {
            console.log(
              '[updateAndFinalize] sales.get returned:',
              currentTransaction,
            );
            // Handle case where sales.get returns empty array (backend filters pending)
            if (
              Array.isArray(currentTransaction) &&
              currentTransaction.length === 0
            ) {
              // Empty array response - transaction is still pending
              // Proceed with second save to finalize
              console.log(
                '[updateAndFinalize] Empty array, proceeding with second save',
              );
              return pos.sendReceipt(false).then((transactionId) => {
                console.log(
                  '[updateAndFinalize] Second save successful, transactionId:',
                  transactionId,
                );
                // Fetch the finalized transaction from backend
                return sales
                  .get(transactionId)
                  .then((finalizedTransaction) => {
                    console.log(
                      '[updateAndFinalize] Fetched finalized transaction',
                    );
                    return finalizedTransaction;
                  })
                  .catch((error) => {
                    // If final sales.get fails (e.g., still pending), return the transactionId
                    // The transaction was saved successfully, we just can't fetch it
                    console.log(
                      '[updateAndFinalize] Final sales.get failed, but transaction was saved:',
                      error,
                    );
                    // Return a minimal transaction object with the ID
                    return { _id: transactionId, type: 'pending' };
                  });
              });
            }

            if (
              currentTransaction.type === 2 ||
              currentTransaction.type === 9
            ) {
              // Already finalized (auto-promoted on first save)
              console.log(
                '[updateAndFinalize] Already finalized, type:',
                currentTransaction.type,
              );
              // Skip second save, just trigger side effects and return
              const transactionType =
                currentTransaction.type === 2 ? 'sale' : 'consignment';
              pos.resetTransaction(transactionType, currentTransaction._id);
              mask.hide();
              navigateTo('pos.route#main');
              broadcaster.send('posFinishedTransaction');
              pos.selectedMenu = 1;
              return currentTransaction;
            } else {
              // Still pending, do second save to finalize
              console.log(
                '[updateAndFinalize] Still pending, proceeding with second save',
              );
              // CRITICAL: Pass false to skipSideEffects for 2nd save
              return pos.sendReceipt(false).then((transactionId) => {
                console.log(
                  '[updateAndFinalize] Second save successful, transactionId:',
                  transactionId,
                );
                // Fetch the finalized transaction from backend
                return sales
                  .get(transactionId)
                  .then((finalizedTransaction) => {
                    console.log(
                      '[updateAndFinalize] Fetched finalized transaction',
                    );
                    return finalizedTransaction;
                  })
                  .catch((error) => {
                    // If final sales.get fails (e.g., still pending), return the transactionId
                    // The transaction was saved successfully, we just can't fetch it
                    console.log(
                      '[updateAndFinalize] Final sales.get failed, but transaction was saved:',
                      error,
                    );
                    // Return a minimal transaction object with the ID
                    return { _id: transactionId, type: 'pending' };
                  });
              });
            }
          })
          .catch((error) => {
            console.error('[updateAndFinalize] sales.get failed:', error);
            // If sales.get rejects (e.g., 'Not found'), treat it as "still pending"
            if (
              error === 'Not found' ||
              (typeof error === 'string' && error.includes('Not found'))
            ) {
              // Transaction is still pending (backend doesn't return pending transactions)
              // Proceed with second save to finalize
              console.log(
                '[updateAndFinalize] Not found error, proceeding with second save',
              );
              return pos.sendReceipt(false).then((transactionId) => {
                console.log(
                  '[updateAndFinalize] Second save successful, transactionId:',
                  transactionId,
                );
                // Fetch the finalized transaction from backend
                return sales
                  .get(transactionId)
                  .then((finalizedTransaction) => {
                    console.log(
                      '[updateAndFinalize] Fetched finalized transaction',
                    );
                    return finalizedTransaction;
                  })
                  .catch((error) => {
                    // If final sales.get fails (e.g., still pending), return the transactionId
                    // The transaction was saved successfully, we just can't fetch it
                    console.log(
                      '[updateAndFinalize] Final sales.get failed, but transaction was saved:',
                      error,
                    );
                    // Return a minimal transaction object with the ID
                    return { _id: transactionId, type: 'pending' };
                  });
              });
            }
            // For other errors, re-throw to let error handling continue
            throw error;
          });
      };

      pos.finalizeTransaction = (transaction) => {
        let receiptNr, displayNr;

        transaction = pos.setWarehouse(transaction);

        transaction.device = currentSession.userId;
        transaction.device_id = pos.selectedDevice.device_id;

        transaction.printed = dateFilter(new Date(), 'short');
        transaction.shopname = locationManager.current.name;

        // Generate a cashback or credit receipt if applicable
        salesCalculations.generatePostSaleCoupon(transaction);

        // Save-twice: pending save already allocated nr/_id — keep them on re-finalize
        // (e.g. PIN cancel then cash) so we do not burn another counter via startNew.
        if (
          transaction._id &&
          transaction.nr &&
          transaction.receiptNr != null
        ) {
          return $q.when(transaction);
        }

        // If consignment use a different number than sales
        if (transaction.payments.consignment === 0) {
          if (
            settingsManager.settings.pos?.backendTransactionInitalization ===
            true
          ) {
            return sales.startNew().then((newTransaction) => {
              transaction.receiptNr = newTransaction.receiptNr;
              transaction.displayNr = newTransaction.displayNr;
              transaction.nr = newTransaction.nr;

              return transaction;
            });
          } else {
            receiptNr = salesManager.counters.sales;
            displayNr = String(salesManager.counters.sales);
          }
        } else {
          if (
            settingsManager.settings.pos?.backendTransactionInitalization ===
            true
          ) {
            return consignment.startNew().then((newTransaction) => {
              transaction.receiptNr = newTransaction.receiptNr;
              transaction.displayNr = newTransaction.displayNr;
              transaction.nr = newTransaction.nr;

              return transaction;
            });
          }
          receiptNr = salesManager.counters.consignments;
          displayNr = 'C' + salesManager.counters.consignments;
        }

        transaction.nr =
          pos.config.cashregisterNr + '-' + posInstance.nr + '-' + displayNr;
        transaction.receiptNr = receiptNr;

        return $q.when(transaction);
      };

      // Add printed property to transactions
      // TOOD add unit test
      pos.setDate = (transaction) => {
        transaction.printed = dateFilter(new Date(), 'short');
      };

      /*
       * This is a global function for keypad key presses. Scope is passed because
       * the value is saved back to the scope that fired the key press. If the value is
       * pos.someProperty then the value is saved in the POS namespace object
       * You can use the on-focus directive on your input to allow the keypad to connect to it.
       */
      pos.key = (val, scope) => {
        function multiIndex(scope, is) {
          // obj,[1,2,3] -> obj[1][2][3]
          return is.length ? multiIndex(scope[is[0]], is.slice(1)) : scope;
        }

        function pathIndex(is) {
          // obj,'1.2.3' -> obj[1][2][3]
          return multiIndex(scope, is);
        }

        if (!scope.currentFocus) {
          return;
        }
        const temp = scope.currentFocus.split('.');

        const prop = temp.pop();
        const objRef = temp.length === 0 ? scope : pathIndex(temp);
        const value = objRef[prop];
        let oldVal = !value ? '' : value;
        if (val == 'C') {
          // Delete last character from focussed input
          const l = oldVal.length;
          if (l > 0) {
            objRef[prop] = oldVal.substring(0, l - 1);
          }
          return;
        }

        //If the text is marked selected, then clear the value
        if (
          scope.$root.textProperties[scope.currentFocus] &&
          scope.textProperties[scope.currentFocus] == value
        ) {
          scope.$root.textProperties[scope.currentFocus] = '';
          oldVal = '';
        }

        objRef[prop] = oldVal + val;
        //Get the object reference from the string representation
        return;
      };

      /*  Save state in localstorage
       */
      pos.persist = (key, value) => {
        if (key == 'DEVICENR') {
          persist.set('posDeviceNr', posInstance.nr);
        } else if (key == 'PIN' && value) {
          persist.set('posPayPlaza', JSON.stringify(value));
        } else if (key == 'TRANS') {
          if (settingsManager.settings.pos?.saveTwicePattern !== true) {
            persist.set('posTransaction', JSON.stringify(value));
          }

          // Update the buffer
          bufferTransaction(value);
        }
      };

      /**
       * @name posApp.pos#resetTransaction
       * @methodOf posApp.lastTransaction
       * @param {string} transactionType transaction type, or dummy value
       * @param {string} transactionId optional transactionId if transaction was saved
       * @returns {Void} nothing
       * @description
       * Reset the transaction.
       */
      // TODO cover with tests
      pos.resetTransaction = (transactionType, transactionId) => {
        //Store copy of the sale/consignment transaction for reprinting and display
        if (transactionType == 'sale' || transactionType == 'consignment') {
          lastTransaction.set(pos.transaction, transactionId);
        }

        //Reset Transaction Object
        pos.startNewTransaction();

        if (transactionType == 'sale') {
          if (
            settingsManager.settings.pos?.backendTransactionInitalization !==
            true
          ) {
            // Increment receipt counter
            salesManager.incrementSalesCounter();
          }
        } else if (transactionType == 'consignment') {
          //Increment consignment counter
          salesManager.incrementConsignmentsCounter();
        }

        pos.selectedPayment = '';

        //Remove finished transactions from local storage
        localStorage.removeItem('posTransaction');

        //Update Second Screen
        secondScreen.showTransaction(pos.transaction);

        //Clean up customer remarks
        pos.customer = null;
      };

      /**
       * @name posApp.pos#loadStoreCreditBalance
       * @methodOf posApp.lastTransaction
       * @param {string} customerId customer Id
       * @returns {Void} nothing
       * @description
       * Reads the store credit value & saves it at the transaction
       */
      pos.loadStoreCreditBalance = (customerId) => {
        // customer store credit
        legacy.getStoreCredit(customerId).then((data) => {
          if (!data || data.error == 'Client not found') {
            displayMessage.message($translate.instant('ClientNumberNotFound'));
            return;
          }

          if (
            !pos.transaction.customer ||
            pos.transaction.customer.id !== customerId
          ) {
            // the date arrive to late, nothing to be done here
            return;
          }

          pos.transaction.customer.storecredit = utility.round2(
            parseFloat(data.value),
          );

          // activate storecredit
          pos.activeStorecredit = false;
        });
      };

      /**
       * @ngdoc
       * @name posApp.pos#selectReceipt
       * @methodOf posApp.pos
       * @param {object, string} receipt fromPage
       * Success callback
       * @description
       * Opens the receipt in the receipt view. fromPage is used to navigate back to
       * the page from which the function was called.
       */
      pos.selectReceipt = (receipt, fromPage) => {
        if (
          receipt.type === '2' ||
          receipt.type === '9' ||
          receipt.type === 'pending'
        ) {
          navigateTo(
            `pos.route#sales-transaction-summary/${
              receipt._id
            }/${encodeURIComponent(fromPage)}`,
          );
          return;
        }

        displayMessage.showLoading();
        retailTransactions
          .filter({
            _id: receipt._id,
          })
          .then((response) => {
            if (response && response.length) {
              let transaction = response[0];

              // sales, withdtawal/deposit, and consignments
              if (transaction.type === 'withdrawal') {
                withdrawalSummary.transaction = transaction;
                withdrawalSummary.fromPage = fromPage;
                navigateTo('pos.route#withdrawal-summary');
              } else if (
                transaction.type === 2 ||
                transaction.type === 9 ||
                transaction.type === 'pending'
              ) {
                navigateTo(
                  `pos.route#sales-transaction-summary/${
                    transaction._id
                  }/${encodeURIComponent(fromPage)}`,
                );
              } else if (transaction.type === 3) {
                // transit orders
                transitOrderSummary.transaction = transaction;
                transitOrderSummary.fromPage = fromPage;
                navigateTo('pos.route#transit-order-summary');
              } else {
                displayMessage.message(
                  $translate.instant('TransactionCouldNotBeOpened'),
                );
                mask.hide();
              }
            } else {
              mask.hide();
              displayMessage.message(
                $translate.instant('TransactionCouldNotBeOpened'),
              );
            }
          });
      };

      /**
       * @ngdoc
       * @name posApp.pos#checkForOpenConsignments
       * @methodOf posApp.pos
       * @param {string} id customer id we want to filter by
       * Success callback
       * @description
       * Asks for filtered consignment transactions by id and status open
       * Returns boolean
       */
      pos.checkForOpenConsignments = (customerId) => {
        if (customerId) {
          handleData
            .getData(
              {
                resource: 'consignments',
              },
              {
                customerId,
                status: 'open',
              },
            )
            .then((data) => {
              pos.openConsignments =
                data?.data?.data?.length > 0 ? true : false;
            });
        }
      };

      // Listens to get the loyalty balance of a customer
      pos.loadLoyaltyBalance = (customerId) => {
        const procGetBalance = (data) => {
          if (!data || !data.saldo) {
            $log.error(
              '[pos.loadLoyaltyBalance] Error getting loyalty points',
              customerId,
            );
            return;
          }

          let points = 0;

          try {
            points = parseInt(data.saldo);
          } catch (e) {
            $log.error(
              '[pos.loadLoyaltyBalance] Error parsing loyalty points',
              customerId,
            );
          }

          pos.setTransactionDetails(pos.transaction, null, null, null, points);
          // calculate customer points
          pos.updateTotals();
        };

        legacy.getClient(customerId).then(procGetBalance);
      };

      pos.reloadTransaction = (transaction) => {
        let message: string;

        if (!transaction) {
          $log.error(
            '[Reload Transaction] Missing required transaction parameter!',
          );
        }

        // Prompt if transaction is in progress.
        if (
          pos.transaction?.customer ||
          (pos.transaction?.receipt.length > 0 &&
            pos.transaction.receipt[0].barcode.substring(1) != transaction.nr)
        ) {
          message = $translate.instant('TransactionInProgressDropTransaction');
          displayMessage.message({
            message: message,
            type: 'prompt',
            callback: (response) => {
              if (response == 'yes') {
                // Reset current transaction
                pos.resetTransaction('reloadTransaction');

                // Attempt again to reload
                pos.reloadTransaction(transaction);
              } else {
                $log.log(
                  '[Reload Transaction] Transaction not loaded because another one is present',
                );
              }
            },
          });
        } else {
          message = $translate.instant('Reloading stored transaction');
          displayMessage.message({
            message: message,
            type: 'info',
          });

          // Start fresh
          transaction = angular.copy(transaction);
          // Cleanup internal properties
          delete transaction.modified;
          delete transaction.created;
          delete transaction._id;
          delete transaction.fpquery;
          delete transaction.fperror;
          // Clear electronic payment information POS-474
          delete transaction.elecTransId;
          delete transaction.merchRef;
          // Delete coupon field
          delete transaction.coupon;
          // Remove any extra fields SWUI-879
          delete transaction.transit;
          // Reset fields
          transaction.pinInfo = '';
          transaction.pinError = '';
          transaction.nr = 0;
          transaction.type = 0;
          delete transaction.shift;

          // Reset payments back to 0 SWUI-924
          for (const i in transaction.payments) {
            transaction.payments[i] = 0.0;
          }

          // Remove all lines that aren't barcodes
          transaction.receipt = _.reject(transaction.receipt, (item) => {
            if (item && item.category != 'barcode') {
              return true;
            }
            return false;
          });
          transaction.couponDiscount = 0;

          $log.log('[Reload Transaction] Loaded transaction');
          pos.selectedMenu = 1;
          pos.transaction = new SaleModel(transaction);

          // Refresh the selected cashier and shop information
          pos.setTransactionDetails(
            pos.transaction,
            pos.config.selectedCashier,
          );

          // Update all totals including loyalty points
          pos.updateTotals();

          if (pos.transaction.customer && pos.transaction.customer.id) {
            // Reload store credit balance
            pos.loadStoreCreditBalance(pos.transaction.customer.id);

            // Reload customer loyalty points
            pos.loadLoyaltyBalance(pos.transaction.customer.id);

            // Check if there are open consignments
            pos.checkForOpenConsignments(pos.transaction.customer.id);

            // Check if customer has open coupons
            broadcaster.send(
              'posGetCustomerOpenCoupons',
              pos.transaction.customer.id,
            );
          }
        }
      };

      return pos;
    },
  );
